import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  PriceStatus,
  ProductChargeType,
  ProductStatus,
  type ProductPrice,
} from "@fitness-app/data-models/entities/Product";

import { getProductPrice } from "~/modules/Products/hooks/useProductTaskAutomation";
import { useAppSelector } from "~/store/initializeStore";

const isSelectableUpsellPrice = (price: ProductPrice) =>
  price.active &&
  price.status !== PriceStatus.Archived &&
  price.paymentClient === "STRIPE" &&
  !(price.installments && price.installments > 1);

/**
 * Options for the success-page upsell target selectors: products owned by the trainer
 * on the same Stripe account as the current one (a saved card cannot be reused across
 * accounts), each with at least one active STRIPE, non-installment price.
 */
export const useUpsellTargetOptions = () => {
  const { t } = useTranslation("products");
  const currentProduct = useAppSelector((store) => store.product.details);
  const { productsListData } = useAppSelector((store) => store.products);

  const eligibleProducts = useMemo(() => {
    if (!currentProduct) {
      return [];
    }

    return productsListData.filter(
      (product) =>
        product.id !== currentProduct.id &&
        product.status === ProductStatus.ACTIVE &&
        product.chargeType !== ProductChargeType.FREE &&
        (product.customStripeAccount ?? null) === (currentProduct.customStripeAccount ?? null) &&
        product.prices?.some(isSelectableUpsellPrice),
    );
  }, [productsListData, currentProduct]);

  const productOptions = useMemo(
    () =>
      eligibleProducts.map((product) => ({
        value: product.id,
        label: product.name,
      })),
    [eligibleProducts],
  );

  const getPriceOptionsFor = useCallback(
    (productId?: string | null) => {
      const product = eligibleProducts.find((item) => item.id === productId);

      if (!product) {
        return [];
      }

      return product.prices.filter(isSelectableUpsellPrice).map(({ type, accessPeriod, id, currency, unit_amount, customLabel }) => ({
        label: `${t(`products:paymentTypeOptions.${type}`)} ${
          type === "recurring" || (type === "one_time" && accessPeriod)
            ? ` (${t(`products:${type}.${accessPeriod?.interval}`, {
                count: accessPeriod?.interval_count,
              })})`
            : ""
        } - ${getProductPrice({
          currency,
          unit_amount,
        })}`.concat(customLabel ? ` - ${customLabel}` : ""),
        value: id,
      }));
    },
    [eligibleProducts, t],
  );

  return { productOptions, getPriceOptionsFor, eligibleProducts };
};
